<!--@format-->
<template>
    <div class="storefront-request-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>Storefront Request</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>

            <div class="content-wrapper">
                <loading-svg v-if="isLoading" class="loading-card" />
                <div v-else class="content">
                    <p class="description">
                        Review storefront details and page to ensure it meets GlassHive's quality
                        standards.
                    </p>
                    <div class="request-details">
                        <div class="title">Request Details</div>
                        <div class="choice-container">
                            <div class="container">
                                <label>Vendor Name</label>
                                <div class="name">
                                    <app-logo :src="agency.logoUrl | companyLogoPlaceholder" />
                                    <p>{{ agency.name }}</p>
                                </div>
                            </div>
                            <div class="container">
                                <label>Website</label>
                                <a class="link" :href="agency.website" target="_blank">
                                    {{ agency.website }}
                                </a>
                            </div>
                            <div class="container">
                                <label>Number of Partner Resources</label>
                                <p>{{ agency.sharedAssetCount }}</p>
                            </div>
                            <div class="container">
                                <label>Joined GlassHive Date</label>
                                <div class="date-container">
                                    <span class="weekday">
                                        {{ agency.joinedDate | momentWeekDay | UpperCaseString }}
                                    </span>
                                    <span class="date">
                                        {{
                                            agency.joinedDate | momentDateMMMDYYYY | UpperCaseString
                                        }}
                                    </span>
                                    <span class="time">{{ agency.joinedDate | momentTime }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="cancel-btn" @click="closeWidget"> Cancel </app-button>
                </div>
                <div class="action-container" v-if="agency.status">
                    <!-- <template v-if="agency.status === 'denied'">
                        <app-button class="save-btn" @click="acceptRequest"> Approve </app-button>
                    </template> -->
                    <template v-if="agency.status === 'approved'">
                        <app-button class="pink" @click="revokeRequest"> Revoke </app-button>
                    </template>
                    <template v-else-if="agency.status === 'pending'">
                        <app-button class="pink" @click="denyRequest"> Deny </app-button>
                        <app-button class="save-btn" @click="acceptRequest"> Accept </app-button>
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const AppLogo = () => import('@/components/common/app-logo');
const LoadingSvg = () => import('@/components/svg/loading-screen-svg');

import { StorefrontService } from '@/scripts/services/storefront/StorefrontService';
import EventBus from '@/scripts/event-bus.js';
import AppPopup from '@/components/popups/app-popup';

export default {
    name: 'storefront-request-widget',

    components: {
        'app-button': AppButton,
        'widget-close-svg': WidgetCloseSvg,
        'app-logo': AppLogo,
        'loading-svg': LoadingSvg,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            agency: {
                id: this.widget.agencyId,
                logoUrl: null,
                joinedDate: null,
                name: null,
                website: null,
                status: null,
                sharedAssetCount: 0,
            },

            isLoading: true,
        };
    },

    computed: {},

    created() {
        const promises = [
            this.getAgency(),
            this.getAccountIdByAgencyId(),
            this.getSharedAssets(),
            this.getStorefrontApplication(),
        ];

        Promise.all(promises).then(() => {
            this.isLoading = false;
        });
    },

    methods: {
        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        getStorefrontApplication() {
            return new Promise((resolve, reject) => {
                StorefrontService.getStorefrontRequestsByAgencyId(this.agency.id)
                    .then((response) => {
                        var application = response.data.data.find((r) => r.Current === true);
                        this.agency.status = application.Status;

                        resolve();
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            'An error occurred while getting your application.',
                            { error },
                        );
                        reject();
                    });
            });
        },

        acceptRequest() {
            StorefrontService.approve(this.agency.id).then(() => {
                this.showAcceptPopup();
                EventBus.$emit('storefront:update');
                this.closeWidget();
            })                    
        },

        showAcceptPopup() {
            this.$modal.show(
                AppPopup,
                {
                    type: 'success-popup success',
                    title: 'Success',
                    content: `<strong>${this.agency.name}</strong> storefront has been accepted and will now be live in<br>the marketplace.`,
                    actions: {
                        confirm: {
                            label: 'Okay',
                            action: () => ({}),
                        },
                    },
                },
                {
                    height: this.$helpers.setPopupHeight(),
                    width: this.$helpers.setPopupWidth(),
                }
            );
        },

        denyRequest() {
            EventBus.$emit('open-widget', {
                name: 'deny-reason-widget',
                agencyId: this.agency.id,
            });
            this.closeWidget();
        },

        revokeRequest() {
            EventBus.$emit('open-widget', {
                name: 'revoke-reason-widget',
                agencyId: this.agency.id,
            });
            this.closeWidget();
        },

        getAgency() {
            return new Promise((resolve, reject) => {
                this.$api
                    .get(`/service/Agencies?$filter=Id eq ${this.agency.id}`)
                    .then((response) => {
                        this.agency.name = response.data.value[0].Name;
                        this.agency.logoUrl = response.data.value[0].LogoImageUrl;
                        this.agency.joinedDate = this.$moment
                            .utc(response.data.value[0].CreatedAt)
                            .local();

                        resolve();
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            'An error occurred while getting your vendor info.',
                            { error },
                        );
                        reject();
                    });
            });
        },

        getAccountIdByAgencyId() {
            return new Promise((resolve, reject) => {
                this.$api
                    .get(`/api/Agencies/${this.agency.id}/Account`)
                    .then((response) => {
                        this.accountId = response.data.Id;
                        this.getWebsite().then(() => {
                            resolve();
                        });
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification(`Failed to retrieve account`, { error });
                        reject();
                    });
            });
        },

        getWebsite() {
            return new Promise((resolve, reject) => {
                this.$api
                    .get(`/api/Account/${this.accountId}/Profile`)
                    .then((response) => {
                        this.agency.website = response.data.Website;
                        resolve();
                    })
                    .catch(() => {
                        reject();
                    });
            });
        },

        getSharedAssets() {
            var filter = `$filter=AgencyClient/AgencyId eq ${this.agency.id}`;
            return new Promise((resolve, reject) => {
                this.$api
                    .get(`/service/SharedAssets?$top=0&${filter}&$count=true`)
                    .then((response) => {
                        this.agency.sharedAssetCount = response.data['@odata.count'];

                        resolve();
                    })
                    .catch(() => {
                        reject();
                    });
            });
        },
    },
};
</script>

<style>
.storefront-request-widget .action-container {
    display: flex;
}

.storefront-request-widget .cancel-btn {
    background: transparent;
    color: #382153;
    border: none;
    width: 127px;
    border: 1px solid #cac6cb;
    border-radius: 20px;
}

.storefront-request-widget .cancel-btn:hover {
    background: #e9e9ef;
}

.storefront-request-widget .widget-contents-container .content-create {
    padding: 0;
}

.storefront-request-widget .content-create .app-text-editor-wrapper,
.storefront-request-widget .content-create .app-text-editor,
.storefront-request-widget .content-create .widget-container {
    height: 100%;
}

.storefront-request-widget .content-create .widget-container .controls {
    display: none;
}

.storefront-request-widget .request-details {
    position: relative;
    border: 2px solid #e9e9ef;
    border-radius: 22px;
    margin-bottom: 25px;
    margin-top: 30px;
    padding: 32px 40px;
}

.storefront-request-widget .title {
    position: absolute;
    top: -11px;
    left: 225px;
    padding: 0px 25px;
    background-color: #ffffff;
    font-weight: 600;
}

.storefront-request-widget .name {
    display: flex;
    align-items: center;
}

.storefront-request-widget .name .app-logo {
    margin-right: 10px;
}

.storefront-request-widget .request-details .container {
    margin-bottom: 24px;
}

.storefront-request-widget .container p {
    margin: 0px;
    font: normal normal normal 16px/11px Roboto;
    color: #382153;
}

.storefront-request-widget .container .link {
    display: block;
    font: normal normal normal 16px/11px Roboto;
    color: #18aeff;
}

.storefront-request-widget .date {
    margin-right: 10px;
    padding-right: 10px;
    border-right: 2px solid #dcdbe0;
}

.storefront-request-widget .date-container {
    margin-bottom: 240px;
}

.storefront-request-widget .week {
    margin-right: 10px;
    padding-right: 10px;
    border-right: 2px solid #dcdbe0;
}

.storefront-request-widget .description {
    font: normal normal normal 16px/20px Roboto;
    letter-spacing: 0px;
    color: #382153;
}

.storefront-request-widget .app-button.pink {
    background: #f30070;
    margin-right: 10px;
}

.storefront-request-widget .app-button.pink:hover {
    background-color: #d10e68;
}

.storefront-request-widget .action-container .app-button {
    width: 148px;
}

.storefront-request-widget .weekday {
    margin-right: 10px;
    padding-right: 10px;
    border-right: 2px solid #dcdbe0;
}
</style>
